import { useMemo, useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslationApp } from "../../../lib/i18next";
import {
  CREATE_ADS_CAMPAIGN_BUDGET_DAILY_QUESTION,
  CREATE_ADS_CAMPAIGN_BUDGET_DESCRIPTION_QUESTION,
  CREATE_ADS_CAMPAIGN_BUDGET_TOTAL_QUESTION,
  CREATE_ADS_CAMPAIGN_DESTINATION_URL_QUESTION,
  CREATE_ADS_CAMPAIGN_IDENTITIES_ACCOUNTS_QUESTION,
  CREATE_ADS_CAMPAIGN_OBJECTIVE_DESCRIPTION,
  CREATE_ADS_CAMPAIGN_OBJECTIVE_QUESTION,
  CREATE_ADS_CAMPAIGN_MEDIA_URL_SERVICES_QUESTION,
  CREATE_ADS_CAMPAIGN_MEDIA_URL_DOCUMENTS_QUESTION,
  CREATE_ADS_CAMPAIGN_TYPE_BUDGET_QUESTION,
  CREATE_ADS_CAMPAIGN_START_END_DATE_QUESTION,
  FIELD_REQUIRED,
  FIELD_MIN_TOTAL_BUDGET_ONE_DOLLAR,
  FIELDS_REQUIRED,
  FIELD_URL_VALID,
  BUTTON_SELECT_CONTENT,
  BUTTON_SELECT_DOCUMENT,
  CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_WITH_OUT_CAROUSEL,
  CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_CAROUSEL,
  CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_DOCUMENTS,
  START_DATE_AND_END_DATE_VALIDATION,
  CREATE_ADS_CAMPAIGN_AD_FORMAT_QUESTION,
} from "../../../i18n/keysTranslations";
import {
  ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION,
  ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_VIDEO_VIEWS_OPTION,
  LINKEDIN,
  MIN_DIMENSIONS_CONTENT,
  IMAGE_FORMATS,
  VIDEO_FORMATS,
  FORMATS_MEDIA_LINKEDIN,
  DOCUMENT_FORMATS_LINKEDIN,
  LINKEDIN_AD_FORMAT_DOCUMENT,
} from "../../../utils/constants";
import { startOfDay } from "date-fns";
import { useDispatchApp } from "../../../lib/redux";
import {
  getCurrencyAdAccountPlatform,
  getIsBlockedActions,
} from "../../../actions/getters";
import { useCreditActionCosts } from "../../useCreditActionCosts";
import { startGetCurrencyTax } from "../../../actions/adsCampaigns";
import { REGEX_URL } from "../../../utils/regex";
import IconButtonAddContent from "../../../components/Buttons/IconButtonAddContent";
import { getPathDatabaseByRole } from "../../../actions/auth";
import {
  formatMediaUrl,
  getMinimumBudget,
} from "../../../services/adsCampaigns";
import { usePlatformDefaultsInGenerateCampaign } from "../../platformDefaults/usePlatformDefaultsInGenerateCampaign";
import { useJumpToLastVisibleStep } from "../useJumpToLastVisibleStep";
import { saveInitialPlatformDefaultsFromCampaign } from "../../platformDefaults/saveInitialPlatformDefaultsFromCampaign";

export function useGenerateCampaignFormLinkedin({
  campaignID,
  generatedPlatforms = {},
  language,
  campaignContext,
  onCallbackSave = () => {},
  onExitToPlatformSelection,
  verifyBlockAlert = true,
  resumeValues = null,
}) {
  const STEPS = [
    "identities",
    "objective",
    "destinationURL",
    "typeBudget",
    "dates",
    "budget",
    "adFormat",
    "mediaUrls",
  ];
  const [error, setError] = useState(null);
  const [currencyTax, setCurrencyTax] = useState(1);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION } = useCreditActionCosts();

  const { userID } = dispatch(getPathDatabaseByRole());

  const { control, watch, setValue, getValues } = useForm({
    defaultValues: {
      step: 0,
      objective: "",
      integrationID: "",
      adAccountID: "",
      destinationURL: "",
      typeBudget: "",
      startDate: null,
      endDate: null,
      budget: "",
      adFormat: "",
      mediaUrls: [],
    },
  });

  const { isLoadingDefaults, resumeSettled } =
    usePlatformDefaultsInGenerateCampaign({
      platform: LINKEDIN,
      setValue,
      getValues,
      resumeValues,
    });

  const step = watch("step");
  const integrationID = watch("integrationID");
  const adAccountID = watch("adAccountID");
  const typeBudget = watch("typeBudget");
  const adFormat = watch("adFormat");
  const mediaUrls = watch("mediaUrls");
  const isDocumentFormat = adFormat === LINKEDIN_AD_FORMAT_DOCUMENT;

  const utils = {
    control,
    setValue,
    watch,
    getValues,
    campaignID,
    campaignContext,
    language,
  };

  const currency = dispatch(
    getCurrencyAdAccountPlatform({
      platform: LINKEDIN,
      integrationID,
      adAccountID,
    })
  );

  const getCurrencyTaxForCurrency = async (curr) => {
    if (!curr) return;
    const { ok, data } = await dispatch(startGetCurrencyTax(curr));
    setCurrencyTax(ok ? data : 1);
  };

  useEffect(() => {
    if (currency) {
      getCurrencyTaxForCurrency(currency);
    }
    // eslint-disable-next-line
  }, [currency]);

  const onFinished = async () => {
    const formValues = getValues();
    const requestObject = {
      ...formValues,
      platform: LINKEDIN,
      typeBudget: formValues.typeBudget,
      adFormat: formValues.adFormat,
      startDate:
        formValues.startDate ||
        (formValues.typeBudget === ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION
          ? startOfDay(new Date())
          : null),
      endDate: formValues.endDate || null,
    };

    if (verifyBlockAlert) {
      const isBlocked = await dispatch(
        getIsBlockedActions({
          action: ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION,
          showUsageConfirmationAlert: true,
          campaignUsageContext: {
            platform: LINKEDIN,
            generatedPlatforms,
          },
        })
      );
      if (isBlocked) {
        return;
      }
    }

    void saveInitialPlatformDefaultsFromCampaign({
      dispatch,
      platform: LINKEDIN,
      formValues: getValues(),
    });

    onCallbackSave(requestObject);
  };

  const fields = useMemo(
    () => ({
      objective: {
        isVisible: () => true,
        utils,
        name: "objective",
        title: CREATE_ADS_CAMPAIGN_OBJECTIVE_QUESTION,
        subtitle: CREATE_ADS_CAMPAIGN_OBJECTIVE_DESCRIPTION,
        showActionsButtons: true,
        showPreviousButton: true,
        showNextButton: true,
        onPreviousStep: () => {
          setError(null);
          setValue("step", step - 1);
        },
        onNextStep: () => {
          if (!getValues("objective")) return setError(t(FIELD_REQUIRED));
          if (
            getValues("objective") === ADS_CAMPAIGN_OBJECTIVE_VIDEO_VIEWS_OPTION
          ) {
            setValue("adFormat", "SINGLE_VIDEO");
            setValue("mediaUrls", []);
          }
          setError(null);
          setValue("step", step + 1);
        },
      },
      identities: {
        isVisible: () => true,
        utils,
        name: "identities",
        title: CREATE_ADS_CAMPAIGN_IDENTITIES_ACCOUNTS_QUESTION,
        showActionsButtons: true,
        showPreviousButton: Boolean(onExitToPlatformSelection),
        showNextButton: true,
        onPreviousStep: () => {
          setError(null);
          onExitToPlatformSelection?.();
        },
        onNextStep: () => {
          if (!getValues("adAccountID")) return setError(t(FIELDS_REQUIRED));
          setError(null);
          setValue("step", step + 1);
        },
      },
      destinationURL: {
        isVisible: () => true,
        utils,
        name: "destinationURL",
        title: CREATE_ADS_CAMPAIGN_DESTINATION_URL_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        showNextButton: true,
        onPreviousStep: () => {
          setError(null);
          setValue("step", step - 1);
        },
        onNextStep: () => {
          const url = getValues("destinationURL");
          if (!url) return setError(t(FIELD_REQUIRED));
          if (!REGEX_URL.test(url)) return setError(t(FIELD_URL_VALID));
          setError(null);
          setValue("step", step + 1);
        },
      },
      typeBudget: {
        isVisible: () => true,
        utils,
        name: "typeBudget",
        title: CREATE_ADS_CAMPAIGN_TYPE_BUDGET_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        showNextButton: true,
        onPreviousStep: () => {
          setError(null);
          setValue("step", step - 1);
        },
        onNextStep: () => {
          const typeBudgetValue = getValues("typeBudget");
          if (!typeBudgetValue) return setError(t(FIELD_REQUIRED));
          setError(null);
          setValue("step", step + 1);
        },
      },
      dates: {
        isVisible: () =>
          typeBudget === ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION,
        utils,
        name: "dates",
        title: CREATE_ADS_CAMPAIGN_START_END_DATE_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        showNextButton: true,
        onPreviousStep: () => {
          setError(null);
          setValue("step", step - 1);
        },
        onNextStep: () => {
          const startDate = getValues("startDate");
          const endDate = getValues("endDate");
          if (!startDate || !endDate) {
            return setError(t(FIELDS_REQUIRED));
          }
          if (startDate > endDate) {
            return setError(t(START_DATE_AND_END_DATE_VALIDATION));
          }
          setError(null);
          setValue("step", step + 1);
        },
      },
      budget: {
        isVisible: () => true,
        utils,
        name: "budget",
        title:
          typeBudget === ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION
            ? CREATE_ADS_CAMPAIGN_BUDGET_DAILY_QUESTION
            : CREATE_ADS_CAMPAIGN_BUDGET_TOTAL_QUESTION,
        subtitle: CREATE_ADS_CAMPAIGN_BUDGET_DESCRIPTION_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        showNextButton: true,
        onPreviousStep: () => {
          setError(null);
          setValue("step", step - 1);
        },
        onNextStep: () => {
          const budget = Number(getValues("budget"));
          if (!getValues("budget") || isNaN(budget)) {
            return setError(t(FIELD_REQUIRED));
          }
          const minimumBudget = getMinimumBudget({
            platform: LINKEDIN,
            typeBudget,
            startDateTime: getValues("startDate"),
            endDateTime: getValues("endDate"),
            currency,
            currencyTax,
          });
          if (minimumBudget != null && budget < minimumBudget) {
            return setError(
              t(FIELD_MIN_TOTAL_BUDGET_ONE_DOLLAR, {
                value: minimumBudget,
                currency: currency,
              })
            );
          }
          setError(null);
          setValue("step", step + 1);
        },
      },
      adFormat: {
        isVisible: () => true,
        utils,
        name: "adFormat",
        title: CREATE_ADS_CAMPAIGN_AD_FORMAT_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        showNextButton: true,
        onPreviousStep: () => {
          setError(null);
          setValue("step", step - 1);
        },
        onNextStep: () => {
          if (!getValues("adFormat")) return setError(t(FIELD_REQUIRED));
          setError(null);
          setValue("step", step + 1);
        },
      },
      mediaUrls: {
        isVisible: () => true,
        utils,
        name: "mediaUrls",
        title: isDocumentFormat
          ? CREATE_ADS_CAMPAIGN_MEDIA_URL_DOCUMENTS_QUESTION
          : CREATE_ADS_CAMPAIGN_MEDIA_URL_SERVICES_QUESTION,
        subtitle: isDocumentFormat
          ? CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_DOCUMENTS
          : adFormat === "CAROUSEL"
            ? CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_CAROUSEL
            : CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_WITH_OUT_CAROUSEL,
        showActionsButtons: true,
        showPreviousButton: true,
        showNextButton: false,
        isFinishQuestion: true,
        disabledFinishButton: !mediaUrls || mediaUrls.length === 0,
        onPreviousStep: () => {
          setError(null);
          setValue("step", step - 1);
        },
        customComponentPreviousNext: (
          <Controller
            name="mediaUrls"
            control={control}
            render={({ field }) => {
              let acceptedFormatsValue = FORMATS_MEDIA_LINKEDIN;
              if (adFormat === "STANDARD_UPDATE" || adFormat === "CAROUSEL") {
                acceptedFormatsValue = IMAGE_FORMATS;
              } else if (adFormat === "SINGLE_VIDEO") {
                acceptedFormatsValue = VIDEO_FORMATS;
              } else if (isDocumentFormat) {
                acceptedFormatsValue = DOCUMENT_FORMATS_LINKEDIN;
              }

              return (
                <IconButtonAddContent
                  mode="button"
                  color="secondary"
                  size="large"
                  minDimensions={isDocumentFormat ? {} : MIN_DIMENSIONS_CONTENT}
                  pathStorage={`adsCampaigns/${userID}/`}
                  pathRTDB={`adsContentsUploaded/${userID}/${campaignID}`}
                  modeUpload="sync"
                  onAddContent={(contents) => {
                    const formatted = contents.map((c) => formatMediaUrl(c));
                    field.onChange([...field.value, ...formatted]);
                  }}
                  acceptedFormats={acceptedFormatsValue}
                  textButton={
                    isDocumentFormat
                      ? BUTTON_SELECT_DOCUMENT
                      : BUTTON_SELECT_CONTENT
                  }
                  showGenerateWithAIOption={!isDocumentFormat}
                  showSocialPostsOption={!isDocumentFormat}
                  showAdAccountOption={!isDocumentFormat}
                  showGalleryOption={!isDocumentFormat}
                  multiple
                  initialValues={{
                    language,
                    campaignContext,
                    platform: LINKEDIN,
                  }}
                  sx={{
                    alignItems: "flex-start",
                    width: { xs: "100%", md: "auto" },
                  }}
                  sxButton={{
                    height: 48,
                    width: "100%",
                  }}
                />
              );
            }}
          />
        ),
        onFinish: () => {
          if (getValues("mediaUrls").length === 0)
            return setError(t(FIELD_REQUIRED));
          setError(null);
          onFinished();
        },
      },
      // eslint-disable-next-line
    }),
    // eslint-disable-next-line
    [
      step,
      utils,
      typeBudget,
      adFormat,
      isDocumentFormat,
      mediaUrls,
      onExitToPlatformSelection,
    ]
  );

  const stepsVisible = STEPS.filter((s) => fields[s].isVisible());
  const { hasJumped } = useJumpToLastVisibleStep({
    enabled: Boolean(resumeValues),
    ready: resumeSettled,
    steps: stepsVisible,
    setValue,
  });
  const isApplyingResume = Boolean(resumeValues) && !hasJumped;

  return {
    steps: stepsVisible,
    fields,
    currentAnswer: isApplyingResume ? null : stepsVisible[step],
    progress: Math.round(((step + 1) / stepsVisible.length) * 100),
    error,
    currency,
    utils,
    isLoadingDefaults,
  };
}
